import {
    MotiviewKillerQuestionOption,
    MotiviewKillerQuestion,
} from 'typings/target_group';

export const MAX_KILLER_QUESTIONS = 5;

/** Yes/No questions have no authored options, so both sides share these ids. */
export const YES_OPTION_ID = 'yes';
export const NO_OPTION_ID = 'no';

export const MIN_MULTIPLE_CHOICE_OPTIONS = 2;
export const MAX_MULTIPLE_CHOICE_OPTIONS = 6;

/** Mirrors the MOTIVIEW_MAX_* constants on the backend. */
export const MAX_QUESTION_TEXT_LENGTH = 300;
export const MAX_OPTION_TEXT_LENGTH = 120;
export const MAX_REDIRECT_URL_LENGTH = 2048;

/**
 * Ids only have to be unique within one group, and the group does not exist
 * yet while the wizard is open, so a counter plus a random suffix is enough.
 * They are never shown to anyone.
 */
let idCounter = 0;
const localId = (prefix: string): string => {
    idCounter += 1;
    return `${prefix}-${idCounter}-${Math.random().toString(36).slice(2, 8)}`;
};

export const newOption = (): MotiviewKillerQuestionOption => ({
    id: localId('opt'),
    text: '',
});

export const newQuestion = (): MotiviewKillerQuestion => ({
    id: localId('q'),
    text: '',
    type: 'YES_NO',
    correct_option_ids: [YES_OPTION_ID],
    must_be_correct: false,
});

/**
 * The redirect target for a rejected candidate.
 *
 * The candidate app assigns this to window.location.href, so anything other
 * than https could execute in the candidate's browser. Credentials are refused
 * too: https://careers.acme.com@evil.com goes to evil.com. The backend
 * enforces both; this copy exists so the recruiter finds out while typing.
 */
export const isValidRedirectUrl = (url: string): boolean => {
    if (!url || !url.trim()) return false;
    if (url.trim().length > MAX_REDIRECT_URL_LENGTH) return false;
    try {
        const parsed = new URL(url.trim());
        if (parsed.username || parsed.password) return false;
        return parsed.protocol === 'https:';
    } catch {
        return false;
    }
};

/**
 * Credentials get their own message because "Enter an https address" is no
 * help at all when the address already is one.
 */
export const redirectUrlError = (url: string): string | null => {
    if (isValidRedirectUrl(url)) return null;

    try {
        const parsed = new URL(url.trim());
        if (parsed.username || parsed.password) {
            return 'Remove the username and password from the address.';
        }
    } catch {
        // Unparseable, so the generic message below is the useful one.
    }

    return 'Enter an https address.';
};

/** How many questions are not already required, which caps the minimum. */
export const optionalQuestionCount = (
    questions: MotiviewKillerQuestion[],
): number => questions.filter((question) => !question.must_be_correct).length;

/** Per-question problems, keyed by question id, for inline display. */
export const validateQuestion = (
    question: MotiviewKillerQuestion,
): string | null => {
    if (!question.text.trim()) return 'Enter the question.';
    // Only reachable by pasting: the input carries maxLength.
    if (question.text.length > MAX_QUESTION_TEXT_LENGTH) {
        return `Keep the question under ${MAX_QUESTION_TEXT_LENGTH} characters.`;
    }

    if (question.type === 'YES_NO') {
        return question.correct_option_ids.length === 1
            ? null
            : 'Choose the correct answer.';
    }

    const options = question.options ?? [];
    if (options.length < MIN_MULTIPLE_CHOICE_OPTIONS) {
        return `Add at least ${MIN_MULTIPLE_CHOICE_OPTIONS} answer options.`;
    }
    if (options.length > MAX_MULTIPLE_CHOICE_OPTIONS) {
        return `A maximum of ${MAX_MULTIPLE_CHOICE_OPTIONS} answer options is allowed.`;
    }
    if (options.some((option) => !option.text.trim()))
        return 'Every answer option needs text.';
    if (options.some((option) => option.text.length > MAX_OPTION_TEXT_LENGTH)) {
        return `Keep each answer option under ${MAX_OPTION_TEXT_LENGTH} characters.`;
    }

    const correct = question.correct_option_ids.filter((id) =>
        options.some((option) => option.id === id),
    );
    if (correct.length === 0) return 'Mark at least one option as correct.';
    // A question where everything is correct cannot be failed, so it is almost
    // certainly a mistake rather than an intent.
    if (correct.length === options.length)
        return 'At least one option must be incorrect.';

    return null;
};

export interface KillerQuestionsValidation {
    isValid: boolean;
    questionErrors: Record<string, string>;
    formError: string | null;
}

/**
 * Whole-section validation, mirroring validateKillerQuestionsConfig on the backend.
 * Returning both per-question and form-level problems lets the wizard show the
 * error next to the field that caused it and still block the create button.
 */
export const validateKillerQuestions = (
    enabled: boolean,
    questions: MotiviewKillerQuestion[],
    minCorrect: number,
    redirectUrl: string,
): KillerQuestionsValidation => {
    if (!enabled) return { isValid: true, questionErrors: {}, formError: null };

    const questionErrors: Record<string, string> = {};
    questions.forEach((question) => {
        const error = validateQuestion(question);
        if (error) questionErrors[question.id] = error;
    });

    let formError: string | null = null;
    if (questions.length === 0) {
        formError = 'Add at least one question, or turn killer questions off.';
    } else if (questions.length > MAX_KILLER_QUESTIONS) {
        formError = `A maximum of ${MAX_KILLER_QUESTIONS} questions is allowed.`;
    }

    // These two block creation without saying anything. The redirect field
    // shows its expected format in the placeholder and turns red on its own,
    // and the minimum is a dropdown the recruiter cannot push out of range, so
    // a written explanation for either would be noise.
    const redirectIsValid = isValidRedirectUrl(redirectUrl);
    const minCorrectIsValid =
        Number.isInteger(minCorrect) &&
        minCorrect >= 0 &&
        minCorrect <= optionalQuestionCount(questions);

    return {
        isValid:
            !formError &&
            Object.keys(questionErrors).length === 0 &&
            redirectIsValid &&
            minCorrectIsValid,
        questionErrors,
        formError,
    };
};

/** Trims authored text so trailing spaces never reach a candidate's screen. */
export const cleanQuestionsForSave = (
    questions: MotiviewKillerQuestion[],
): MotiviewKillerQuestion[] =>
    questions.map((question) => ({
        id: question.id,
        text: question.text.trim(),
        type: question.type,
        ...(question.type === 'MULTIPLE_CHOICE' && {
            options: (question.options ?? []).map((option) => ({
                id: option.id,
                text: option.text.trim(),
            })),
        }),
        correct_option_ids: question.correct_option_ids,
        must_be_correct: question.must_be_correct,
    }));
